import { useState, useEffect, useCallback } from 'react'
import { CertificationsService } from '../services/api/certificationsService'
import { Certification } from '../types/certification'
import { logger } from '../utils/logger'

interface UseCertificationsReturn {
  certifications: Certification[]
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

// Global cache for certifications
let certificationsCache: Certification[] | null = null
let cachePromise: Promise<Certification[]> | null = null

export const useCertifications = (): UseCertificationsReturn => {
  const [certifications, setCertifications] = useState<Certification[]>(certificationsCache || [])
  const [isLoading, setIsLoading] = useState(!certificationsCache)
  const [error, setError] = useState<string | null>(null)

  const fetchCertifications = useCallback(async (): Promise<Certification[]> => {
    // If we already have a pending request, return that promise
    if (cachePromise) {
      return cachePromise
    }

    // If we have cached data, return it
    if (certificationsCache) {
      return certificationsCache
    }

    // Create new request
    cachePromise = CertificationsService.getCertifications()

    try {
      const data = await cachePromise
      certificationsCache = data
      setCertifications(data)
      setError(null)
      return data
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch certifications'
      logger.error('Error fetching certifications:', err)
      setError(errorMessage)
      throw err
    } finally {
      cachePromise = null
      setIsLoading(false)
    }
  }, [])

  const refetch = useCallback(async () => {
    // Clear cache to force refresh
    certificationsCache = null
    cachePromise = null
    setIsLoading(true)
    await fetchCertifications()
  }, [fetchCertifications])

  useEffect(() => {
    if (!certificationsCache && !cachePromise) {
      fetchCertifications()
    } else if (certificationsCache) {
      // Data already cached
      setCertifications(certificationsCache)
      setIsLoading(false)
    }
  }, [fetchCertifications])

  return {
    certifications,
    isLoading,
    error,
    refetch
  }
}